import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import LocationPicker from '../components/LocationPicker';
import { 
  Wind, Clock, MapPin, User, Phone, Loader2,
  Plus, Check, Flame, CreditCard, Map
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ShishaRentalPage = () => {
  const { t, language } = useLanguage();
  const { token, user } = useAuth();
  const navigate = useNavigate();

  const [shishas, setShishas] = useState([]);
  const [flavors, setFlavors] = useState([]);
  const [shishaExtras, setShishaExtras] = useState([]);
  const [settings, setSettings] = useState(null);
  const [platformStatus, setPlatformStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    shisha_id: '',
    flavor_id: '',
    duration_hours: 2,
    extra_charcoal: false,
    extra_hose: false,
    selected_extras: {}, // { extra_id: quantity }
    delivery_location: '',
    location_type: 'hotel',
    delivery_time: '',
    customer_name: user?.name || '',
    phone: user?.phone || '',
    notes: '',
    payment_method: 'card'
  });
  const [gpsCoordinates, setGpsCoordinates] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [shishaRes, flavorRes, settingsRes, extrasRes, cfgRes] = await Promise.all([
          fetch(`${API_URL}/api/shishas`),
          fetch(`${API_URL}/api/shisha-flavors`),
          fetch(`${API_URL}/api/settings`),
          fetch(`${API_URL}/api/shisha-extras`),
          fetch(`${API_URL}/api/config/public`)
        ]);

        if (shishaRes.ok) {
          const data = await shishaRes.json();
          setShishas(data);
          const firstAvailable = data.find(s => s.is_available !== false);
          if (firstAvailable) {
            setFormData(prev => ({ ...prev, shisha_id: firstAvailable.shisha_id }));
          }
        }

        if (flavorRes.ok) {
          const data = await flavorRes.json();
          setFlavors(data);
          const firstAvailable = data.find(f => f.is_available !== false);
          if (firstAvailable) {
            setFormData(prev => ({ ...prev, flavor_id: firstAvailable.flavor_id }));
          }
        }

        if (settingsRes.ok) {
          const data = await settingsRes.json();
          setSettings(data);
        }

        if (extrasRes.ok) {
          const data = await extrasRes.json();
          setShishaExtras(data);
        }

        if (cfgRes.ok) {
          setPlatformStatus(await cfgRes.json());
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Auto-fill user data when logged in
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        customer_name: user.name || prev.customer_name,
        phone: user.phone || prev.phone,
      }));
    }
  }, [user]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const selectedShisha = shishas.find(s => s.shisha_id === formData.shisha_id);
  const selectedFlavor = flavors.find(f => f.flavor_id === formData.flavor_id);

  const calculatePrices = () => {
    if (!selectedShisha || !settings) return { rental: 0, delivery: 0, extras: 0, deposit: 0, total: 0 };

    const rental = selectedShisha.base_price * formData.duration_hours;
    const delivery = formData.location_type === 'beach' 
      ? settings.beach_delivery_fee 
      : settings.standard_delivery_fee;
    let extras = 0;
    if (formData.extra_charcoal) extras += settings.charcoal_price;
    if (formData.extra_hose) extras += settings.extra_hose_price;
    if (selectedFlavor) extras += selectedFlavor.price;
    // Dynamic admin-managed extras
    Object.entries(formData.selected_extras || {}).forEach(([extraId, qty]) => {
      const ex = shishaExtras.find(e => e.extra_id === extraId);
      if (ex && qty > 0) extras += ex.price * qty;
    });
    const deposit = selectedShisha.deposit;
    const total = rental + delivery + extras + deposit;

    return { rental, delivery, extras, deposit, total };
  };

  const prices = calculatePrices();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Require authentication
    if (!user) {
      toast.error(language === 'pt' ? 'Faca login para alugar' : 'Please login to rent');
      navigate('/login?redirect=/shisha');
      return;
    }
    
    setSubmitting(true);

    try {
      const headers = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const selectedExtrasArr = Object.entries(formData.selected_extras || {})
        .filter(([_, q]) => q > 0)
        .map(([extra_id, quantity]) => ({ extra_id, quantity }));

      const rentalData = {
        ...formData,
        selected_extras: selectedExtrasArr,
        total: prices.total,
        subtotal: prices.rental + prices.extras,
        delivery_fee: prices.delivery,
        deposit: prices.deposit,
        gps_coordinates: gpsCoordinates || null,
        payment_status: 'processing'
      };

      const response = await fetch(`${API_URL}/api/shisha-rentals`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(rentalData)
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to create rental');
      }

      const rental = await response.json();

      // If paying with card, redirect to Stripe
      if (formData.payment_method === 'card') {
        const checkoutResponse = await fetch(`${API_URL}/api/payments/create-checkout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            origin_url: window.location.origin,
            order_id: rental.rental_id,
            amount: prices.total,
            order_type: 'shisha'
          })
        });

        if (!checkoutResponse.ok) {
          throw new Error('Failed to create payment session');
        }

        const checkoutData = await checkoutResponse.json();
        window.location.href = checkoutData.url;
        return;
      }

      // Cash
      try {
        const { saveActiveOrder } = await import('../utils/activeOrder');
        saveActiveOrder(rental.rental_id, 'shisha');
      } catch (e) { /* ignore */ }
      toast.success(
        language === 'pt' ? 'Aluguer criado! Pague na entrega.' : 'Rental created! Pay on delivery.'
      );
      navigate(`/order-tracking/${rental.rental_id}?type=shisha`);

    } catch (error) {
      console.error('Rental error:', error);
      toast.error(
        error?.message || (language === 'pt' ? 'Erro ao processar aluguer' : 'Error processing rental'),
        { duration: 8000 }
      );
    } finally {
      setSubmitting(false);
    }
  };

  const locationTypes = [
    { id: 'hotel', label: t('hotel') },
    { id: 'apartment', label: t('apartment') },
    { id: 'beach', label: t('beach') },
    { id: 'marina', label: t('marina') },
    { id: 'residential', label: t('residential') },
  ];

  const durationOptions = [1, 2, 3, 4, 5, 6];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] py-8">
        <div className="container mx-auto px-4">
          <Skeleton className="h-12 w-64 mb-8" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <Skeleton className="h-64 rounded-3xl mb-6" />
              <Skeleton className="h-48 rounded-3xl" />
            </div>
            <Skeleton className="h-96 rounded-3xl" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Hero */}
      <div 
        className="relative h-64 bg-cover bg-center"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1527661591475-527312dd65f5?w=1200')`
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-[#004E64]/60 to-[#004E64]/80" />
        <div className="relative container mx-auto px-4 h-full flex items-center">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <Wind className="text-white" size={32} />
              <span className="font-handwritten text-2xl text-white/90">Premium Experience</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white" data-testid="shisha-title">
              {t('rentShisha')}
            </h1>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {platformStatus && platformStatus.platform_is_open === false && (
          <div className="mb-6 flex items-start gap-3 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3" data-testid="shisha-closed-banner">
            <Wind size={18} className="mt-0.5 shrink-0 text-amber-600" />
            <div>
              <p className="font-semibold text-amber-900 text-sm">
                {language === 'pt' ? 'Fechado de momento' : 'Currently closed'}
              </p>
              <p className="text-amber-800 text-xs mt-0.5">
                {language === 'pt'
                  ? `A plataforma está fechada neste momento.${platformStatus.platform_hours?.is_enabled ? ` Horário: ${platformStatus.platform_hours?.open}\u2013${platformStatus.platform_hours?.close}.` : ''} Não é possível concluir alugueres.`
                  : `The platform is closed right now. Rentals can't be completed.`}
              </p>
            </div>
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Selection */}
            <div className="lg:col-span-2 space-y-6">
              {/* Shisha Models */}
              <Card className="rounded-3xl border-[#004E64]/5">
                <CardHeader>
                  <CardTitle className="text-[#004E64]">{t('selectModel')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {shishas.map(shisha => (
                      <div
                        key={shisha.shisha_id}
                        onClick={() => { if (shisha.is_available !== false) handleChange('shisha_id', shisha.shisha_id); }}
                        className={`relative rounded-2xl overflow-hidden transition-all ${
                          shisha.is_available === false
                            ? 'border border-[#004E64]/10 opacity-50 grayscale cursor-not-allowed'
                            : formData.shisha_id === shisha.shisha_id 
                              ? 'ring-2 ring-[#FF6B35] shadow-lg cursor-pointer' 
                              : 'border border-[#004E64]/10 hover:shadow-md cursor-pointer'
                        }`}
                        data-testid={`shisha-${shisha.shisha_id}`}
                      >
                        <img 
                          src={shisha.image} 
                          alt={shisha.name}
                          className="w-full h-32 object-cover"
                        />
                        {shisha.is_available === false && (
                          <span className="absolute top-2 left-2 text-xs font-semibold text-white bg-red-500 px-2 py-0.5 rounded-full" data-testid={`shisha-unavailable-${shisha.shisha_id}`}>
                            {language === 'pt' ? 'Indisponível' : 'Unavailable'}
                          </span>
                        )}
                        <div className="p-3">
                          <div className="flex items-center justify-between mb-1">
                            <h4 className="font-bold text-[#004E64]">{shisha.name}</h4>
                            {shisha.type === 'premium' && (
                              <span className="badge-popular text-xs">Premium</span>
                            )}
                          </div>
                          <p className="text-[#FF6B35] font-bold">
                            €{shisha.base_price.toFixed(2)}/hr
                          </p>
                        </div>
                        {formData.shisha_id === shisha.shisha_id && (
                          <div className="absolute top-2 right-2 w-6 h-6 bg-[#FF6B35] rounded-full flex items-center justify-center">
                            <Check size={14} className="text-white" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Flavors */}
              <Card className="rounded-3xl border-[#004E64]/5">
                <CardHeader>
                  <CardTitle className="text-[#004E64]">{t('selectFlavor')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {flavors.map(flavor => (
                      <div
                        key={flavor.flavor_id}
                        onClick={() => { if (flavor.is_available !== false) handleChange('flavor_id', flavor.flavor_id); }}
                        className={`p-3 rounded-xl transition-all text-center ${
                          flavor.is_available === false
                            ? 'bg-[#F4F1EA] text-[#9FA2B4] opacity-50 cursor-not-allowed line-through'
                            : formData.flavor_id === flavor.flavor_id 
                              ? 'bg-[#FF6B35] text-white cursor-pointer' 
                              : 'bg-[#F4F1EA] text-[#004E64] hover:bg-[#004E64]/10 cursor-pointer'
                        }`}
                        data-testid={`flavor-${flavor.flavor_id}`}
                      >
                        <p className="font-semibold">{flavor.name}</p>
                        {flavor.price > 0 && (
                          <p className="text-sm opacity-80">+€{flavor.price.toFixed(2)}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Duration & Extras */}
              <Card className="rounded-3xl border-[#004E64]/5">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-[#004E64]">
                    <Clock className="text-[#FF6B35]" />
                    {t('rentalDuration')} & {t('extras')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>{t('rentalDuration')}</Label>
                    <Select
                      value={formData.duration_hours.toString()}
                      onValueChange={(v) => handleChange('duration_hours', parseInt(v))}
                    >
                      <SelectTrigger className="rounded-xl mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {durationOptions.map(hours => (
                          <SelectItem key={hours} value={hours.toString()}>
                            {hours} {t('hours')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex flex-wrap gap-4">
                    <label 
                      className={`flex items-center gap-3 p-4 rounded-xl cursor-pointer transition-all ${
                        formData.extra_charcoal 
                          ? 'bg-[#FF6B35]/10 border-2 border-[#FF6B35]' 
                          : 'bg-[#F4F1EA] border-2 border-transparent'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.extra_charcoal}
                        onChange={(e) => handleChange('extra_charcoal', e.target.checked)}
                        className="sr-only"
                      />
                      <Flame className={formData.extra_charcoal ? 'text-[#FF6B35]' : 'text-[#9FA2B4]'} />
                      <div>
                        <p className="font-semibold text-[#004E64]">{t('extraCharcoal')}</p>
                        <p className="text-sm text-[#9FA2B4]">+€{settings?.charcoal_price?.toFixed(2) || '3.00'}</p>
                      </div>
                    </label>

                    <label 
                      className={`flex items-center gap-3 p-4 rounded-xl cursor-pointer transition-all ${
                        formData.extra_hose 
                          ? 'bg-[#FF6B35]/10 border-2 border-[#FF6B35]' 
                          : 'bg-[#F4F1EA] border-2 border-transparent'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.extra_hose}
                        onChange={(e) => handleChange('extra_hose', e.target.checked)}
                        className="sr-only"
                      />
                      <Plus className={formData.extra_hose ? 'text-[#FF6B35]' : 'text-[#9FA2B4]'} />
                      <div>
                        <p className="font-semibold text-[#004E64]">{t('extraHose')}</p>
                        <p className="text-sm text-[#9FA2B4]">+€{settings?.extra_hose_price?.toFixed(2) || '5.00'}</p>
                      </div>
                    </label>
                  </div>
                </CardContent>
              </Card>

              {/* Dynamic Admin-managed Extras */}
              {shishaExtras.length > 0 && (
                <Card className="rounded-3xl border-[#004E64]/5" data-testid="dynamic-extras-section">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-[#004E64]">
                      <Plus className="text-[#FF6B35]" />
                      {language === 'pt' ? 'Extras Adicionais' : 'Additional Extras'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {shishaExtras.map(extra => {
                        const qty = formData.selected_extras?.[extra.extra_id] || 0;
                        const max = extra.max_quantity || 5;
                        return (
                          <div
                            key={extra.extra_id}
                            className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${
                              qty > 0 ? 'border-[#FF6B35] bg-[#FF6B35]/5' : 'border-transparent bg-[#F4F1EA]'
                            }`}
                            data-testid={`extra-card-${extra.extra_id}`}
                          >
                            {extra.image && (
                              <img
                                src={extra.image.startsWith('http') ? extra.image : `${API_URL}${extra.image}`}
                                alt={extra.name}
                                className="w-12 h-12 rounded-lg object-cover"
                              />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-[#004E64] text-sm truncate">{extra.name}</p>
                              {extra.description && (
                                <p className="text-xs text-[#9FA2B4] truncate">{extra.description}</p>
                              )}
                              <p className="text-sm text-[#FF6B35] font-bold">+€{extra.price?.toFixed(2)}</p>
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                className="h-7 w-7 rounded-full"
                                onClick={() => {
                                  const newQty = Math.max(0, qty - 1);
                                  setFormData(prev => ({
                                    ...prev,
                                    selected_extras: { ...prev.selected_extras, [extra.extra_id]: newQty }
                                  }));
                                }}
                                disabled={qty === 0}
                                data-testid={`extra-decrement-${extra.extra_id}`}
                              >
                                -
                              </Button>
                              <span className="w-6 text-center font-semibold text-[#004E64]">{qty}</span>
                              <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                className="h-7 w-7 rounded-full"
                                onClick={() => {
                                  const newQty = Math.min(max, qty + 1);
                                  setFormData(prev => ({
                                    ...prev,
                                    selected_extras: { ...prev.selected_extras, [extra.extra_id]: newQty }
                                  }));
                                }}
                                disabled={qty >= max}
                                data-testid={`extra-increment-${extra.extra_id}`}
                              >
                                +
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Delivery Info */}
              <Card className="rounded-3xl border-[#004E64]/5">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-[#004E64]">
                    <MapPin className="text-[#FF6B35]" />
                    {t('deliveryLocation')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>{t('name')} *</Label>
                      <Input
                        value={formData.customer_name}
                        onChange={(e) => handleChange('customer_name', e.target.value)}
                        required
                        className="rounded-xl mt-1"
                      />
                    </div>
                    <div>
                      <Label>{t('phone')} *</Label>
                      <Input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleChange('phone', e.target.value)}
                        required
                        className="rounded-xl mt-1"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>{t('locationType')} *</Label>
                    <Select
                      value={formData.location_type}
                      onValueChange={(v) => handleChange('location_type', v)}
                    >
                      <SelectTrigger className="rounded-xl mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {locationTypes.map(type => (
                          <SelectItem key={type.id} value={type.id}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>{t('deliveryLocation')} *</Label>
                    <Input
                      value={formData.delivery_location}
                      onChange={(e) => handleChange('delivery_location', e.target.value)}
                      required
                      className="rounded-xl mt-1"
                      data-testid="shisha-delivery-location-input"
                    />
                  </div>

                  {/* Map Location Picker — same as the food checkout */}
                  <div className="p-4 bg-[#2EC4B6]/10 rounded-2xl">
                    <div className="flex items-center gap-2 mb-3">
                      <Map size={18} className="text-[#2EC4B6]" />
                      <Label className="text-[#004E64] font-semibold text-sm">
                        {language === 'pt' ? 'Marcar localização exata no mapa *' : 'Mark exact location on map *'}
                      </Label>
                    </div>
                    <LocationPicker
                      value={formData.delivery_location}
                      onChange={(value) => handleChange('delivery_location', value)}
                      onCoordinatesChange={setGpsCoordinates}
                      language={language}
                      placeholder={language === 'pt' ? 'Pesquisar ou clicar no mapa...' : 'Search or click on map...'}
                    />
                    {gpsCoordinates && (
                      <div className="mt-2 p-2 bg-white rounded-xl flex items-center gap-2" data-testid="shisha-gps-display">
                        <MapPin size={14} className="text-[#2EC4B6]" />
                        <span className="text-xs text-[#004E64]">GPS: {gpsCoordinates}</span>
                      </div>
                    )}
                  </div>

                  <div>
                    <Label>{t('deliveryTime')} *</Label>
                    <Input
                      type="time"
                      value={formData.delivery_time}
                      onChange={(e) => handleChange('delivery_time', e.target.value)}
                      required
                      className="rounded-xl mt-1"
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <Card className="rounded-3xl border-[#004E64]/5 sticky top-24">
                <CardHeader>
                  <CardTitle className="text-[#004E64]">
                    {language === 'pt' ? 'Resumo do Aluguer' : 'Rental Summary'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {selectedShisha && (
                    <div className="flex items-center gap-3 p-3 bg-[#F4F1EA] rounded-xl">
                      <img 
                        src={selectedShisha.image} 
                        alt={selectedShisha.name}
                        className="w-16 h-16 rounded-xl object-cover"
                      />
                      <div>
                        <p className="font-bold text-[#004E64]">{selectedShisha.name}</p>
                        <p className="text-sm text-[#9FA2B4]">{formData.duration_hours} {t('hours')}</p>
                      </div>
                    </div>
                  )}

                  {selectedFlavor && (
                    <div className="flex justify-between">
                      <span className="text-[#9FA2B4]">{language === 'pt' ? 'Sabor' : 'Flavor'}</span>
                      <span className="text-[#004E64] font-medium">{selectedFlavor.name}</span>
                    </div>
                  )}

                  <Separator />

                  <div className="flex justify-between text-[#9FA2B4]">
                    <span>{language === 'pt' ? 'Aluguer' : 'Rental'}</span>
                    <span>€{prices.rental.toFixed(2)}</span>
                  </div>

                  <div className="flex justify-between text-[#9FA2B4]">
                    <span>{t('deliveryFee')}</span>
                    <span>€{prices.delivery.toFixed(2)}</span>
                  </div>

                  {prices.extras > 0 && (
                    <div className="space-y-1" data-testid="rental-summary-extras">
                      <div className="flex justify-between text-[#9FA2B4]">
                        <span>{t('extras')}</span>
                        <span>€{prices.extras.toFixed(2)}</span>
                      </div>
                      {Object.entries(formData.selected_extras || {}).filter(([, q]) => q > 0).map(([id, qty]) => {
                        const ex = shishaExtras.find(e => e.extra_id === id);
                        if (!ex) return null;
                        return (
                          <div key={id} className="flex justify-between text-xs text-[#9FA2B4] pl-3">
                            <span>{qty}× {ex.name}</span>
                            <span>€{(ex.price * qty).toFixed(2)}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {prices.deposit > 0 && (
                    <div className="flex justify-between text-[#9FA2B4]">
                      <span>{t('deposit')}</span>
                      <span>€{prices.deposit.toFixed(2)}</span>
                    </div>
                  )}

                  <Separator />

                  <div className="flex justify-between text-lg">
                    <span className="font-bold text-[#004E64]">{t('total')}</span>
                    <span className="font-bold text-[#FF6B35]">€{prices.total.toFixed(2)}</span>
                  </div>

                  {/* Payment Method Selection */}
                  <div className="mt-2">
                    <p className="text-sm font-semibold text-[#004E64] mb-2">
                      {language === 'pt' ? 'Metodo de Pagamento' : 'Payment Method'}
                    </p>
                    <div className="space-y-2">
                      {[
                        { value: 'card', label: language === 'pt' ? 'Cartao' : 'Card', icon: CreditCard, desc: 'Visa, Mastercard' }
                      ].map(method => (
                        <button
                          key={method.value}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, payment_method: method.value }))}
                          className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-colors ${
                            formData.payment_method === method.value
                              ? 'border-[#FF6B35] bg-[#FF6B35]/5'
                              : 'border-[#004E64]/10 hover:border-[#FF6B35]/30'
                          }`}
                          data-testid={`payment-${method.value}`}
                        >
                          <method.icon size={20} className={formData.payment_method === method.value ? 'text-[#FF6B35]' : 'text-[#9FA2B4]'} />
                          <div className="text-left">
                            <p className={`text-sm font-semibold ${formData.payment_method === method.value ? 'text-[#FF6B35]' : 'text-[#004E64]'}`}>{method.label}</p>
                            <p className="text-xs text-[#9FA2B4]">{method.desc}</p>
                          </div>
                          {formData.payment_method === method.value && (
                            <Check size={16} className="ml-auto text-[#FF6B35]" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full py-6 text-lg font-bold"
                    data-testid="rent-now-button"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="mr-2 animate-spin" />
                        {language === 'pt' ? 'Processando...' : 'Processing...'}
                      </>
                    ) : (
                      t('rentNow')
                    )}
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ShishaRentalPage;
